import type {
  CommitmentResponse,
  MyCommitmentResponse,
  PaginatedResponse,
  UpdatePaymentMethodResponse,
} from '@@/types/api';
import { useApi } from './use-api';

// The commitment mutation endpoints return their resource UNWRAPPED (the
// backend resources set `$wrap = null`), so there is no `{ data }` envelope to
// destructure. Declaring `{ data: X }` here type-checks fine and yields
// `undefined` at runtime — see #1980. The contract tests in
// `__tests__/use-commitments-api.test.ts` pin these shapes against the base
// MSW handlers.
export function useCommitmentsApi() {
  const { get, post, put, del } = useApi('/api/v1/events');

  return {
    getMyCommitment: (eventId: string) =>
      get<CommitmentResponse | null>(`/${eventId}/commitments/mine`),
    getMyCommitments: (params?: { page?: number; per_page?: number }) =>
      get<PaginatedResponse<MyCommitmentResponse>>('/commitments/mine', {
        params,
      }),
    commitToEvent: (
      eventId: string,
      body: Record<string, unknown>,
      idempotencyKey?: string,
    ) =>
      post<CommitmentResponse>(`/${eventId}/commitments`, {
        body,
        ...(idempotencyKey
          ? { headers: { 'Idempotency-Key': idempotencyKey } }
          : {}),
      }),
    withdrawCommitment: (eventId: string, id: string) =>
      del<undefined>(`/${eventId}/commitments/${id}`),
    updatePaymentMethod: (id: string, body: Record<string, unknown>) =>
      put<UpdatePaymentMethodResponse>(`/commitments/${id}/payment-method`, {
        body,
      }),
    switchType: (
      eventId: string,
      commitmentId: string,
      body: Record<string, unknown>,
    ) =>
      put<CommitmentResponse>(
        `/${eventId}/commitments/${commitmentId}/switch-type`,
        { body },
      ),
  };
}
